import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  deletePasskey,
  listPasskeys,
  passkeysSupported,
  registerPasskey,
  totpConfirm,
  totpDisable,
  totpEnroll,
  totpStatus,
  type PasskeyInfo,
  type TotpEnrollment,
} from "../auth/methods";
import { Button, TextField } from "../compose/controls";
import { KeyRound, ShieldCheck } from "../compose/Icon";

/**
 * Account security `/settings/security` — enrol / manage the second factors:
 * an authenticator-app TOTP (with recovery codes) and passkeys (WebAuthn). All
 * flows hit the real backend; the page is authenticated (RouteGuard).
 */
export function Security() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [totpOn, setTotpOn] = useState<boolean | null>(null);
  const [enroll, setEnroll] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([totpStatus(), listPasskeys()]);
      setTotpOn(s.enrolled);
      setPasskeys(list);
    } catch {
      setError("Couldn't load your security settings.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function beginTotp() {
    setError(null);
    setBusy(true);
    try {
      setEnroll(await totpEnroll());
    } catch {
      setError("Couldn't start TOTP enrolment.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await totpConfirm(code.trim());
      if (!res.confirmed) return setError("That code didn't match. Try again.");
      setEnroll(null);
      setCode("");
      await refresh();
    } catch {
      setError("Couldn't confirm the code.");
    } finally {
      setBusy(false);
    }
  }

  async function disableTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await totpDisable(disableCode.trim());
      if (!res.disabled) return setError("That code didn't match — 2FA is still on.");
      setDisableCode("");
      await refresh();
    } catch {
      setError("Couldn't disable 2FA.");
    } finally {
      setBusy(false);
    }
  }

  async function addPasskey() {
    setError(null);
    setBusy(true);
    try {
      const ok = await registerPasskey(nickname.trim() || "Passkey");
      if (!ok) return setError("Passkey registration was cancelled.");
      setNickname("");
      await refresh();
    } catch {
      setError("Couldn't add a passkey on this device.");
    } finally {
      setBusy(false);
    }
  }

  async function removePasskey(id: number) {
    setError(null);
    try {
      await deletePasskey(id);
      await refresh();
    } catch {
      setError("Couldn't remove that passkey.");
    }
  }

  return (
    <div className="settings">
      <div className="settings__inner">
        <button
          type="button"
          className="settings__back"
          onClick={() => navigate(-1)}
          style={{ background: "none", border: 0, cursor: "pointer", marginBottom: 12 }}
        >
          &larr; Back
        </button>
        <h1 className="settings__title">Account security</h1>
        <p className="dialog__sub" style={{ marginBottom: 24 }}>
          Signed in as <strong>{user?.email}</strong>. Add a second factor to
          protect your account.
        </p>

        {error ? (
          <div className="auth__error" role="alert" data-testid="sec-error">
            {error}
          </div>
        ) : null}

        {/* ── TOTP ─────────────────────────────────────────────────────── */}
        <section className="settings__card" data-testid="sec-totp">
          <div className="settings__cardhead">
            <ShieldCheck size={18} strokeWidth={1.5} />
            <h2>Authenticator app (TOTP)</h2>
          </div>

          {totpOn == null ? (
            <p className="dialog__sub">Loading…</p>
          ) : totpOn ? (
            <>
              <p className="dialog__sub">
                Two-factor authentication is <strong>on</strong>. Enter a current
                code to turn it off.
              </p>
              <form onSubmit={disableTotp} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <TextField
                  label="Current code"
                  inputMode="numeric"
                  placeholder="123456"
                  value={disableCode}
                  data-testid="sec-disable-code"
                  onChange={(e) => setDisableCode(e.target.value)}
                />
                <Button type="submit" variant="secondary" disabled={busy} data-testid="sec-disable">
                  Disable 2FA
                </Button>
              </form>
            </>
          ) : enroll ? (
            <>
              <p className="dialog__sub">
                Scan the QR in your authenticator app, or enter this key manually:
              </p>
              <code className="settings__key" data-testid="sec-manual-key">
                {enroll.manual_key}
              </code>
              <details style={{ margin: "8px 0" }}>
                <summary>otpauth URI</summary>
                <code className="settings__uri">{enroll.otpauth_uri}</code>
              </details>
              <p className="dialog__sub">
                <strong>Recovery codes</strong> — store these safely; each works
                once if you lose your device:
              </p>
              <ul className="settings__recovery" data-testid="sec-recovery">
                {enroll.recovery_codes.map((rc) => (
                  <li key={rc}>
                    <code>{rc}</code>
                  </li>
                ))}
              </ul>
              <form onSubmit={confirmTotp} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <TextField
                  label="Enter a code to confirm"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  data-testid="sec-confirm-code"
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                />
                <Button type="submit" variant="primary" disabled={busy} data-testid="sec-confirm">
                  Confirm & enable
                </Button>
              </form>
            </>
          ) : (
            <>
              <p className="dialog__sub">
                Not enabled. Use an app like 1Password, Authy, or Google
                Authenticator.
              </p>
              <Button variant="primary" disabled={busy} onClick={beginTotp} data-testid="sec-enroll">
                Enable 2FA
              </Button>
            </>
          )}
        </section>

        {/* ── Passkeys ─────────────────────────────────────────────────── */}
        <section className="settings__card" data-testid="sec-passkeys">
          <div className="settings__cardhead">
            <KeyRound size={18} strokeWidth={1.5} />
            <h2>Passkeys</h2>
          </div>
          {!passkeysSupported() ? (
            <p className="dialog__sub">This browser doesn&rsquo;t support passkeys.</p>
          ) : (
            <>
              {passkeys == null ? (
                <p className="dialog__sub">Loading…</p>
              ) : passkeys.length === 0 ? (
                <p className="dialog__sub">No passkeys yet.</p>
              ) : (
                <ul className="settings__passkeys">
                  {passkeys.map((pk) => (
                    <li key={pk.id} className="settings__passkey">
                      <div>
                        <strong>{pk.nickname || "Passkey"}</strong>
                        <span className="settings__muted">
                          {" "}
                          · added {pk.created_at.slice(0, 10)}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        data-testid={`sec-passkey-del-${pk.id}`}
                        onClick={() => removePasskey(pk.id)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 12 }}>
                <TextField
                  label="Add a passkey"
                  placeholder="e.g. MacBook Touch ID"
                  value={nickname}
                  data-testid="sec-passkey-nick"
                  onChange={(e) => setNickname(e.target.value)}
                />
                <Button variant="primary" disabled={busy} onClick={addPasskey} data-testid="sec-passkey-add">
                  Add passkey
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
